import Mnoga, { LookupOptions } from '@engoo/mnoga';
import { LookupData } from '@engoo/mnoga/utils/i18n';
import { List } from 'immutable';
import moment from 'moment-timezone';

// NOTE: When adding/removing locales, make sure to also update the list of Moment.js locales to
// include (via `ContextReplacementPlugin` in `config/webpack/constants`).
//
// For some reason, the chinese locales don't work if you don't explicitly import them... Maybe it's
// a bug with Webpack?
import 'moment/locale/zh-cn';
import 'moment/locale/zh-tw';

// Import translation files.
import en from 'config/locales/en.json';
import es from 'config/locales/es.json';
import id from 'config/locales/id.json';
import it from 'config/locales/it.json';
import ja from 'config/locales/ja.json';
import ko from 'config/locales/ko.json';
import ptBR from 'config/locales/pt-BR.json';
import ru from 'config/locales/ru.json';
import th from 'config/locales/th.json';
import tr from 'config/locales/tr.json';
import vi from 'config/locales/vi.json';
import zhHans from 'config/locales/zh-Hans.json';
import zhHant from 'config/locales/zh-Hant.json';

// TODO: Make timezone enum?
export type TimezoneType = string;

// TODO: Make locale enum?
export type LocaleType = string;

type InputDate = Date | number;

type AllowedDateFormats = (
  // YYYY-MM-DD is used by isSameDay, and not shown to users.
  'L' | 'LL' | 'LLL' | 'LLLL' | 'LT' | 'l' | 'll' | 'lll' | 'llll' | 'LTS' | 'YYYY-MM-DD'
);

export type tFunctionType = LocalizationService['t'];
export type getNameFunctionType =
  (t: tFunctionType, data?: LookupData, options?: LookupOptions) => string;

/**
 * Extend Mnoga to include our phrases, aliases, fallbacks and custom timezone property.
 */
export class LocalizationService extends Mnoga {
  private timezone: TimezoneType | undefined = undefined;
  private fallbackTimezone: TimezoneType = 'GMT';

  public constructor() {
    super();

    // Initialize the translations.
    this.setPhrases('en', en);
    this.setPhrases('es', es);
    this.setPhrases('id', id);
    this.setPhrases('it', it);
    this.setPhrases('ja', ja);
    this.setPhrases('ko', ko);
    this.setPhrases('pt-BR', ptBR);
    this.setPhrases('ru', ru);
    this.setPhrases('th', th);
    this.setPhrases('tr', tr);
    this.setPhrases('vi', vi);
    this.setPhrases('zh-Hans', zhHans);
    this.setPhrases('zh-Hant', zhHant);

    // These are replacements for locales that we have translations for, but don't technically
    // support. For example, 'zh-HK' should show traditional chinese, as opposed to simplified
    // chinese. However, the script subtag is not specified (for browsers like Firefox).
    this.setAlias(['pt', 'pt-PT'], 'pt-BR');
    this.setAlias(['zh', 'zh-CN'], 'zh-Hans');
    this.setAlias(['zh-HK', 'zh-MO', 'zh-TW', 'yue'], 'zh-Hant');

    this.setFallback('en');
    this.setLocale('en');
  }

  /**
   * Returns the timezone set by `setTimezone` or the timezone
   * used by user's browser's `Intl` API.
   * In the case when `Intl` (eg. IE) is not supported, it returns
   * a correspondng fallback timezone based on user's brand location.
   */
  public getTimezone(
    getBrowserTimezone: () => TimezoneType | undefined = () => (
      typeof Intl === 'object' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined
    ),
  ): TimezoneType {
    return this.timezone || getBrowserTimezone() || this.fallbackTimezone;
  }

  /**
   * Set the timezone. You can set it to `undefined` to make
   * `getTimezone` return a best estimate for local timezone.
   */
  public setTimezone(tz: TimezoneType | undefined) {
    if (tz !== this.timezone) {
      this.timezone = tz;
      this.callSubscribers();
    }
  }

  public getFallbackTimezone(): TimezoneType {
    return this.fallbackTimezone;
  }

  public setFallbackTimezone(tz: TimezoneType) {
    if (tz !== this.fallbackTimezone) {
      this.fallbackTimezone = tz;
      this.callSubscribers();
    }
  }

  /**
   * Used to format a date in the current locale. Currently a wrapper for moment().format.
   * Please refer to the Localized formats here: http://momentjs.com/docs/#/displaying/format/
   */
  public formatDate(
    date: InputDate,
    format: AllowedDateFormats = 'LL',
    tz: TimezoneType = this.getTimezone(),
  ): string {
    return moment.tz(
      date,
      tz,
    ).locale(this.getLocale()).format(format);
  }

  /**
   * Used to humanize a duration in the current locale.
   * https://momentjs.com/docs/#/durations/humanize/
   *
   * @param durationMs Number of milliseconds the event elapsed.
   */
  public formatDuration(durationMs: number): string {
    const locale = this.getLocale();
    return moment.duration(durationMs).locale(locale).humanize();
  }

  /**
   * Generates a relative time given a specific date.
   */
  public fromNow(date: InputDate, dropSuffix: boolean = false): string {
    const locale = this.getLocale();
    return moment(date).locale(locale).fromNow(dropSuffix);
  }

  public fromOther(date: InputDate, other: InputDate, dropSuffix: boolean = false): string {
    const locale = this.getLocale();
    return moment(date).locale(locale).from(other, dropSuffix);
  }

  public isSameDay(date1: InputDate, date2: InputDate, tz?: TimezoneType): boolean {
    return this.formatDate(date1, 'YYYY-MM-DD', tz) === this.formatDate(date2, 'YYYY-MM-DD', tz);
  }

  /**
   * Sets the locales in the order of preference for translating strings, formatting numbers and
   * dates. This method does not maintain the list of locales the user puts in, but rather a
   * list of locales that best match the supported locales.
   *
   * Passed locales are expected to follow conventions specified in:
   * https://tools.ietf.org/html/bcp47#section-2.1.1
   *
   * This overrides the setLocale() method of Mnoga to support taking an Immutable.js List as an
   * argument.
   */
  public setLocale(ls: LocaleType | LocaleType[] | List<LocaleType>) {
    if (List.isList(ls)) {
      super.setLocale(ls.toArray());
    } else {
      super.setLocale(ls);
    }
  }
}

// Moment (as of version 2.17.1) doesn't have locales for zhHans or zhHant. Have them inherit
// zh-ch and zh-tw, respectively. https://momentjs.com/docs/#/customization/.
moment.defineLocale('zh', { parentLocale: 'zh-cn' });

// defineLocale does not normalize the specified locale (bug, I think), so make zh-hant lowercase.
moment.defineLocale('zh-hant', { parentLocale: 'zh-tw' });

// Explicitly importing moment locales, as is done in this file, and calling `moment.defineLocale` both have
// the side effect of changing the default moment locale to whichever locale was related to the operation.
// Set the default back to English.
moment.locale('en');

// this makes stuff like 1 day and 23 hours round off to 1 day ago, 1 hour 50 minutes to 1 hour ago.
// we do it like that to keep things "fresh"
moment.relativeTimeRounding(Math.floor);
